import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ChevronDown, ArrowRight } from "lucide-react";
import SEO from "../lib/seo";
import { BACKEND } from "../lib/api";
import { SOLUTIONS } from "../data/solutions";
import { APPROACH } from "../data/site";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import SolutionDiagram from "../components/SolutionDiagram";
import CTASection, { PrimaryButton } from "../components/CTASection";

const IMAGES = {
  "data-centre-infrastructure": "/images/solutions/data-centre-infrastructure.jpg",
  "network-connectivity": "/images/solutions/network-connectivity.jpg",
  "cybersecurity-resilience": "/images/solutions/cybersecurity-resilience.jpg",
  "cloud-digital-workplace": "/images/solutions/cloud-digital-workplace.jpg",
  "collaboration-av-iot": "/images/solutions/collaboration-av-iot.jpg",
  "software-data-digital-engineering": "/images/solutions/software-data-digital-engineering.jpg",
  "managed-it-technology-services": "/images/solutions/managed-it-technology-services.jpg",
};

const OEM_NAMES: Record<string, string> = {
  "dell-technologies": "Dell Technologies", "hpe": "HPE", "netapp": "NetApp", "pure-storage": "Pure Storage",
  "rittal": "Rittal", "legrand": "Legrand", "apc": "APC", "eaton": "Eaton", "vertiv": "Vertiv", "riello": "Riello",
  "veeam": "Veeam", "commvault": "Commvault", "rubrik": "Rubrik", "acronis": "Acronis",
  "vmware": "VMware", "microsoft": "Microsoft", "nutanix": "Nutanix", "commscope": "CommScope", "hikvision": "Hikvision",
};

export default function SolutionPage({ slug }) {
  const pillar = useMemo(() => SOLUTIONS.find((s) => s.slug === slug), [slug]);
  const [openFaq, setOpenFaq] = useState(0);
  const [faqs, setFaqs] = useState(pillar ? pillar.faqs : []);

  useEffect(() => {
    if (!pillar) return;
    setOpenFaq(0);
    setFaqs(pillar.faqs);
    fetch(`${BACKEND}/api/faqs?scope=${pillar.slug}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.faqs && d.faqs.length) {
          setFaqs(d.faqs.map((f) => ({ q: f.question, a: f.answer })));
        }
      })
      .catch(() => {});
  }, [pillar, slug]);

  if (!pillar) {
    return (
      <div className="min-h-screen bg-[#F6F8FA] flex items-center justify-center pt-24">
        <p className="text-mist">Solution not found.</p>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${pillar.title} — Aviokan Technologies`,
    serviceType: pillar.title,
    provider: { "@type": "Organization", name: "Aviokan Technologies Private Limited", url: "https://www.aviokan.com" },
    areaServed: "India",
    description: pillar.heroDesc,
  };

  return (
    <div data-testid="solution-page">
      <SEO
        title={`${pillar.title} Solutions`}
        description={pillar.heroDesc}
        path={`/solutions/${pillar.slug}`}
        type="website"
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow={`SOLUTION PILLAR ${pillar.num}`}
        title={`${pillar.shortTitle}\n${pillar.realTagline}`}
        desc={pillar.heroDesc}
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Solutions", to: "/#pillars" }, { label: pillar.shortTitle }]}
        image={`/images/solutions/${slug}.jpg`}
        testId="solution-hero"
      >
        <PrimaryButton to="/contact" testId="solution-hero-cta">Talk to an Expert <ArrowUpRight size={15} /></PrimaryButton>
      </PageHero>

      {/* Business Challenges */}
      <section className="bg-[#F6F8FA] py-24 border-t border-[#E2E7EF]" data-testid="business-challenges">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">BUSINESS CHALLENGES</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight max-w-2xl">
              Problems this pillar exists to solve.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {pillar.challenges.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="h-full border border-[#E2E7EF] bg-white p-7 hover:border-cy/40 transition-colors duration-300">
                  <span className="font-tech text-xs text-ember">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display font-bold text-lg text-[#101828] mt-4 leading-snug">{c.title}</h3>
                  <p className="text-sm text-mist/60 leading-relaxed mt-3">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solution */}
      <section className="bg-[#EEF2F7] py-24 border-y border-[#E2E7EF]" data-testid="our-solution">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">OUR SOLUTION</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight">
              How Aviokan addresses it.
            </h2>
            <p className="mt-7 text-base text-mist/75 leading-relaxed">{pillar.solution}</p>
            <div className="mt-8 border border-cy/25 bg-white/60 p-6">
              <p className="text-[11px] font-tech tracking-[0.25em] text-[#0089A1] mb-3">CAPABILITIES INCLUDED</p>
              <div className="flex flex-wrap gap-1.5">
                {pillar.services.slice(0, 8).map((s) => (
                  <span key={s} className="text-[10px] font-tech border border-[#E2E7EF] text-mist/70 px-2.5 py-1">{s}</span>
                ))}
                <span className="text-[10px] font-tech border border-ember/30 text-ember px-2.5 py-1">+{pillar.services.length - 8} more below</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#E2E7EF] p-2.5">
              <img src={IMAGES[pillar.slug]} alt={`${pillar.title} — enterprise technology implementation by Aviokan`} loading="lazy" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-[#F6F8FA] py-24" data-testid="capabilities">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">CAPABILITIES</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight mb-12">
              Everything under this pillar.
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {pillar.services.map((s, i) => (
              <Reveal key={s} delay={(i % 4) * 0.04}>
                <div className="flex items-center gap-3 border border-[#E2E7EF] bg-white px-4 py-4 hover:border-cy/40 transition-colors duration-300">
                  <span className="h-1.5 w-1.5 rotate-45 bg-ember shrink-0" />
                  <span className="text-sm text-mist font-medium">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Components Covered — from the official company profile */}
      {/* Signature chain — page-specific dark visual (Resilience Chain on DC, per-practice chains elsewhere) */}
      {pillar.signature && (
        <section className="bg-[#14151A] py-24 overflow-hidden" data-testid="signature-section">
          <div className="grain" aria-hidden="true" />
          <div className="mx-auto max-w-[1320px] px-5 md:px-10 relative">
            <Reveal>
              <p className="text-xs font-tech tracking-[0.35em] text-cy mb-5">{pillar.signature.eyebrow}</p>
              <h2 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight max-w-2xl">{pillar.signature.title}</h2>
              <p className="text-sm text-white/60 mt-4 max-w-xl">{pillar.signature.sub}</p>
            </Reveal>
            <div className="mt-12 grid lg:grid-cols-2 gap-6">
              {pillar.signature.chains.map((chain) => (
                <Reveal key={chain.label}>
                  <div className="h-full rounded-md border border-white/15 bg-[#0B0C10]/70 p-8" data-testid={`signature-chain-${chain.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="text-[10px] font-tech tracking-[0.28em] text-cy mb-6">{chain.label}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      {chain.flow.map((f, fi) => (
                        <span key={f} className="flex items-center gap-2">
                          <span className="border border-white/15 bg-white/5 text-white text-xs md:text-sm font-bold px-3 py-2 rounded-sm hover:border-ember/60 transition-colors duration-300">{f}</span>
                          {fi < chain.flow.length - 1 && <span className="h-1.5 w-1.5 rotate-45 bg-ember" aria-hidden="true" />}
                        </span>
                      ))}
                    </div>
                    <p className="mt-6 text-xs text-white/45 font-tech leading-relaxed">{chain.note}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      <section className="bg-[#EEF2F7] py-24 border-y border-[#E2E7EF]" data-testid="components-covered">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">COMPONENTS COVERED</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight mb-6 max-w-2xl">
              Every component, specified.
            </h2>
            <p className="text-sm text-mist/60 max-w-2xl leading-relaxed mb-12">
              The full component breakdown Aviokan supplies, installs and supports under this practice — from facilities and hardware to software and management tooling.
            </p>
          </Reveal>
          {pillar.stack && (
            <Reveal delay={0.05}>
              <div className="border border-cy/25 bg-white p-6 mb-8" data-testid="tech-stack">
                <p className="text-[11px] font-tech tracking-[0.25em] text-[#0089A1] mb-4">TECHNOLOGY STACK</p>
                <div className="flex flex-wrap gap-2">
                  {pillar.stack.map((t) => (
                    <span key={t} className="text-[11px] font-tech border border-[#E2E7EF] bg-[#F6F8FA] text-[#0A1F44] px-3 py-1.5">{t}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillar.components.map((g, i) => (
              <Reveal key={g.label} delay={(i % 3) * 0.06}>
                <div className="h-full border border-[#E2E7EF] bg-white p-7" data-testid={`component-group-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-tech text-xs text-ember">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-display font-bold text-[#101828] text-base leading-tight">{g.label}</h3>
                  </div>
                  {g.oems && (
                    <div className="mt-5 pt-4 border-t border-[#F0F3F7]">
                      <p className="text-[9px] font-tech tracking-[0.24em] text-mist/40 mb-2">KEY OEMs</p>
                      <div className="flex flex-wrap items-center gap-2">
                        {g.oems.map((oslug, oi) => {
                          const oemName = OEM_NAMES[oslug] || oslug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                          return (
                            <Link key={oslug} to={`/partners/${oslug}`} title={`${oemName} — view partner profile`} className="inline-flex items-center gap-1.5 border border-[#E2E7EF] bg-white px-2 py-1 hover:border-cy/60 hover:text-cy transition-colors duration-200" data-testid={`component-oem-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${oi}`}>
                              <img src={`/logos/oem/${oslug}.png`} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-4 w-4 object-contain" />
                              <span className="text-[10px] font-tech text-mist/70">{oemName}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {g.items.map((it) => (
                      <span key={it} className="text-[10px] font-tech border border-[#E2E7EF] text-mist/65 px-2 py-1">{it}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OEM Technology Ecosystem */}
      <section className="bg-[#F6F8FA] py-20" data-testid="pillar-oems">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-4">TECHNOLOGY ECOSYSTEM</p>
                <h2 className="font-display font-black text-2xl md:text-3xl text-[#101828] tracking-tight max-w-xl">
                  OEM platforms integrated under this practice.
                </h2>
              </div>
              <Link to="/partners" className="text-xs font-bold text-[#0089A1] hover:text-ember transition-colors whitespace-nowrap">
                Browse the full ecosystem →
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="flex flex-wrap gap-2">
              {(pillar.oems || []).map((o) => (
                <span key={o} className="inline-flex items-center gap-2 border border-[#E2E7EF] bg-white px-4 py-2 text-[12px] font-bold text-mist/75 hover:text-[#101828] hover:border-ember/50 transition-colors duration-300" data-testid={`pillar-oem-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="h-1.5 w-1.5 rotate-45 bg-cy shrink-0" /> {o}
                </span>
              ))}
            </div>
            <p className="mt-6 text-xs text-mist/40">Shown as “Technology Ecosystem” — partnership status is labeled only when verified.</p>
          </Reveal>
        </div>
      </section>

      {/* Architecture Diagram */}
      <section className="relative bg-[#0B0C10] py-24 overflow-hidden" data-testid="architecture-section">
        <div className="grain" aria-hidden="true" />
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 relative">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-cy mb-5">SOLUTION ARCHITECTURE</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-white tracking-tight max-w-2xl">
              The reference architecture we design from.
            </h2>
            <p className="text-sm text-white/55 mt-4 max-w-xl">
              Each layer carries the one above it — which is why we engineer all four as one system, on certified platforms.
            </p>
          </Reveal>
          <div className="mt-14">
            <SolutionDiagram diagram={pillar.diagram} />
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="bg-[#F6F8FA] py-24" data-testid="approach">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">OUR APPROACH</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight mb-12">A disciplined delivery method.</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {APPROACH.map((a, i) => (
              <Reveal key={a.step} delay={i * 0.05}>
                <div className="h-full border-t-2 border-ember/60 bg-white pt-4 pb-5 px-4" data-testid={`approach-mini-${a.step.toLowerCase()}`}>
                  <span className="font-tech text-[10px] text-mist/40">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-display font-bold text-[#101828] text-sm mt-2">{a.step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes + Industries */}
      <section className="bg-[#EEF2F7] py-24 border-y border-[#E2E7EF]" data-testid="outcomes">
        <div className="mx-auto max-w-[1320px] px-5 md:px-10 grid lg:grid-cols-[1.2fr_0.8fr] gap-14">
          <div>
            <Reveal>
              <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5">OUTCOMES</p>
              <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight mb-10">What your business gains.</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pillar.outcomes.map((o, i) => (
                <Reveal key={o} delay={i * 0.05}>
                  <div className="flex items-center gap-3.5 border border-[#E2E7EF] bg-white/60 px-5 py-4" data-testid={`outcome-${o.toLowerCase().replace(/\s+/g, "-")}`}>
                    <CheckCircle2 size={17} className="text-[#0089A1] shrink-0" />
                    <span className="text-sm font-semibold text-[#101828]">{o}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="border border-[#E2E7EF] bg-white/60 p-8 h-full">              <h3 className="font-display font-bold text-xl text-[#101828]">Relevant industries</h3>
              <div className="mt-6 flex flex-wrap gap-2">
                {pillar.industries.map((ind) => (
                  <Link key={ind} to="/clients" className="text-xs font-tech border border-cy/25 text-[#0089A1] px-3 py-1.5 hover:bg-cy hover:text-[#14151A] transition-colors">{ind}</Link>
                ))}
              </div>
              <div className="mt-8 border-t border-[#E2E7EF] pt-6">
                <p className="text-sm text-mist/60 leading-relaxed">
                  Every engagement follows the same promise: technology integrated around your business outcomes — never technology for its own sake.
                </p>
                <Link to="/about/approach" className="inline-flex items-center gap-2 text-[#0089A1] text-sm font-bold mt-5 hover:text-ember transition-colors">
                  See our full approach <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F6F8FA] py-24" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="text-xs font-tech tracking-[0.35em] text-[#0089A1] mb-5 text-center">FAQ</p>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#101828] tracking-tight mb-12 text-center">
              Questions about {pillar.shortTitle.toLowerCase()}.
            </h2>
          </Reveal>
          <div className="divide-y divide-[#E4E8EF] border-y border-[#E2E7EF]">
            {faqs.map((f, i) => (
              <div key={i}>
                <button
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  data-testid={`faq-question-${i}`}
                >
                  <span className="font-display font-bold text-[#101828] text-base md:text-lg group-hover:text-[#0089A1] transition-colors">{f.q}</span>
                  <ChevronDown size={18} className={`text-[#0089A1] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <p className="pb-7 text-sm text-mist/70 leading-relaxed pr-10" data-testid={`faq-answer-${i}`}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={`Let's build the right ${pillar.shortTitle.toLowerCase()} architecture for your business.`} />
    </div>
  );
}
